import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SEO } from '../components/SEO';
import { breadcrumbSchema } from '../utils/structuredData';
export function PrivacyPolicyPage() {
  const structuredData = [breadcrumbSchema([{
    name: 'Home',
    url: 'https://close-crm-agentur.de'
  }, {
    name: 'Privacy Policy',
    url: 'https://close-crm-agentur.de/en/privacy-policy'
  }])];
  return <>
      <SEO title="Privacy Policy" description="Privacy policy for Close CRM Agentur and CRM Kraft LLC." canonical="https://close-crm-agentur.de/en/privacy-policy" noindex={true} structuredData={structuredData} data-id="element-615" data-mp="2it8rr5ntleji" />
      <div className="min-h-screen bg-[#0a0a0a]" data-id="element-616" data-mp="1v34qh4y3tqvo">
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-lg border-b border-white/5" data-id="element-617" data-mp="1v3eggsj5fy1d">
          <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-618" data-mp="1z6kh9knhsx0g">
            <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20 gap-2 sm:gap-4" data-id="element-619" data-mp="29qbn5aao628w">
              <a href="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 sm:flex-1" aria-label="Close CRM Agentur homepage" data-id="element-620" data-mp="24rqr7n2ga4q9">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#0ea5e9] flex items-center justify-center flex-shrink-0" data-id="element-621" data-mp="2er96hq7f12bz">
                  <span className="text-white font-bold text-xs sm:text-sm" data-id="element-622" data-mp="1qysnndfut6bm">
                    C
                  </span>
                </div>
                <span className="text-white font-bold text-sm sm:text-base lg:text-lg whitespace-nowrap" data-id="element-623" data-mp="20r6pe66xmcxs">
                  Close CRM Agentur
                </span>
              </a>
              <LanguageSwitcher className="absolute right-3 sm:relative sm:right-0" data-id="element-624" data-mp="18adv2lpw1k8p" />
              <a href="/" className="hidden sm:flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors text-sm flex-shrink-0" data-id="element-625" data-mp="13bkhyueijv92">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-626" data-mp="1x11sye27ir2y" />
                Back to homepage
              </a>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-627" data-mp="21sjscxol0jlt">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-628" data-mp="26r3d4urfvpcl">
            <div className="prose prose-invert prose-lg max-w-none" data-id="element-629" data-mp="26qbhjjxudrmb">
              <h1 className="text-4xl font-bold text-white mb-8" data-id="element-630" data-mp="1a1vq1ib6986k">
                Privacy Policy
              </h1>
              <div className="space-y-8 text-gray-300" data-id="element-631" data-mp="1e2xss94as2da">
                <section data-id="element-632" data-mp="1j0ze70p74drx">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-633" data-mp="1j0qyz25lowdk">
                    1. Privacy at a glance
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-634" data-mp="1nyit82tpwukx">
                    General information
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-635" data-mp="2zf8ztn21zof2">
                    The following information provides a simple overview of what
                    happens to your personal data when you visit this website.
                    Personal data is any data that can be used to identify you
                    personally. Detailed information on data protection can be
                    found in this privacy policy.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-636" data-mp="9390xqxdsabk">
                    Data collection on this website
                  </h3>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-637" data-mp="2b7g2kkjl78">
                    Who is responsible for data collection?
                  </h4>
                  <p className="leading-relaxed mb-4" data-id="element-638" data-mp="3r9dxgqoo7lfv">
                    Data processing on this website is carried out by the
                    website operator. You can find the operator&apos;s contact
                    details in the “Data controller” section of this privacy
                    policy.
                  </p>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-639" data-mp="1sjn6woj7vv6o">
                    How do we collect your data?
                  </h4>
                  <p className="leading-relaxed mb-4" data-id="element-640" data-mp="1xh5kwtc3d0lj">
                    Some data is collected when you provide it to us, for
                    example when you contact us. Other data is collected
                    automatically or with your consent when you visit the
                    website. This is primarily technical data such as your
                    browser, operating system, or the time you accessed the
                    page.
                  </p>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-641" data-mp="zvhldzkca25s">
                    What do we use your data for?
                  </h4>
                  <p className="leading-relaxed mb-4" data-id="element-642" data-mp="14u2me17f0pva">
                    Some data is collected to ensure the website is provided
                    without errors. Other data may be used to analyse how you
                    use the website.
                  </p>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-643" data-mp="c8cmazcgt1zt">
                    What rights do you have?
                  </h4>
                  <p className="leading-relaxed" data-id="element-644" data-mp="7ab74pi5tmtm">
                    You have the right to receive information about the origin,
                    recipient, and purpose of your stored personal data free of
                    charge at any time. You also have the right to request
                    correction or deletion of this data, withdraw consent for
                    future processing, request restriction of processing, and
                    lodge a complaint with the competent supervisory authority.
                  </p>
                </section>

                <section data-id="element-645" data-mp="zy6xzdl1skih">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-646" data-mp="zz07unfazq4u">
                    2. Hosting
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-647" data-mp="18xllc45x4eyr">
                    Webflow
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-648" data-mp="6kyxa7t5d633">
                    We host our website with Webflow, Inc., 398 11th Street, 2nd
                    Floor, San Francisco, CA 94103, USA. When you visit our
                    website, Webflow may collect log files including your IP
                    address.
                  </p>
                  <p className="leading-relaxed mb-4" data-id="element-649" data-mp="kcvma2ziqzv9">
                    Webflow is used to create and host websites. It may store
                    cookies or similar technologies necessary for displaying the
                    website, providing specific functions, and ensuring
                    security.
                  </p>
                  <p className="leading-relaxed" data-id="element-650" data-mp="13a6h9be4b74w">
                    For more information, please see Webflow&apos;s privacy
                    policy:{' '}
                    <a href="https://webflow.com/legal/eu-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline" data-id="element-651" data-mp="304jdrivrk06o">
                      https://webflow.com/legal/eu-privacy-policy
                    </a>
                    .
                  </p>
                </section>

                <section data-id="element-652" data-mp="2ite8ml8n8ngk">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-653" data-mp="2iu5s4xoyuuqh">
                    3. General information and mandatory disclosures
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-654" data-mp="3gesbj8kuighn">
                    Data protection
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-655" data-mp="36ifhg9le20md">
                    We treat your personal data confidentially and in accordance
                    with applicable data protection laws and this privacy
                    policy. Please note that data transmission on the internet,
                    for example through email communications, may have security
                    gaps.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-656" data-mp="3q5wzflf3axwj">
                    Data controller
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-657" data-mp="3v3yjo9o9539l">
                    The data controller responsible for processing on this
                    website is:
                  </p>
                  <p className="leading-relaxed mb-4" data-id="element-658" data-mp="3m5wm643q8sr2">
                    CRM Kraft LLC
                    <br data-id="element-659" data-mp="dquqq65d9wm1" />
                    Represented by: Justus Kraft
                    <br data-id="element-660" data-mp="hren37i48w74" />
                    7901 4th St. N, Ste. 300
                    <br data-id="element-661" data-mp="3zrp938izfm8" />
                    St. Petersburg, FL 33702
                    <br data-id="element-662" data-mp="3zi1lmaydkjp" />
                    United States of America
                    <br data-id="element-663" data-mp="8xtc7z92nz2z" />
                    Phone:{' '}
                    <a href="tel:+491745444423" className="text-[#0ea5e9] hover:underline" data-id="element-664" data-mp="wofipm3fhbav">
                      +49 174 5444423
                    </a>
                    <br data-id="element-665" data-mp="1atfpv2esv1sw" />
                    Email:{' '}
                    <a href="mailto:justus@crm-kraft.de" className="text-[#0ea5e9] hover:underline" data-id="element-666" data-mp="1aruwvh84vqx5">
                      justus@crm-kraft.de
                    </a>
                  </p>
                  <p className="leading-relaxed mb-4" data-id="element-667" data-mp="1flpe8e09ntt2">
                    The data controller is the natural or legal person who alone
                    or jointly with others determines the purposes and means of
                    processing personal data.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-668" data-mp="11tsqsn9jm830">
                    Storage period
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-669" data-mp="wteu21de6kka">
                    Unless a more specific storage period is stated in this
                    privacy policy, your personal data will remain with us until
                    the purpose for processing no longer applies. If you request
                    deletion or withdraw consent, we will delete your data
                    unless there are legally permitted reasons to retain it.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-670" data-mp="16t3s6sny9a2e">
                    SSL/TLS encryption
                  </h3>
                  <p className="leading-relaxed" data-id="element-671" data-mp="11tzmic08hugt">
                    This website uses SSL/TLS encryption to protect the
                    transmission of confidential content. You can recognise an
                    encrypted connection when your browser&apos;s address bar
                    changes from “http://” to “https://” and displays a lock
                    symbol.
                  </p>
                </section>

                <section data-id="element-672" data-mp="iy16gtkfzmq5">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-673" data-mp="2i9anhf154drh">
                    4. Data collection on this website
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-674" data-mp="2dah79eugy8nm">
                    Cookies
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-675" data-mp="2lovlsflt2yqj">
                    Our website uses cookies. Cookies are small text files that
                    do not harm your device. They may be stored temporarily for
                    a session or permanently until you delete them or your
                    browser deletes them automatically.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-676" data-mp="22tf1lsorl379">
                    Contact requests
                  </h3>
                  <p className="leading-relaxed" data-id="element-677" data-mp="26v8v9h0jl6xx">
                    If you contact us by email or phone, we store and process
                    the information you provide, including your contact details,
                    to handle your request and any follow-up questions. We do
                    not share this information without your consent.
                  </p>
                </section>

                <section data-id="element-678" data-mp="32imr6mmkbevn">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-679" data-mp="32htvcgp569sa">
                    5. Plugins and tools
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-680" data-mp="2xj0dkc2tnwmb">
                    YouTube
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-681" data-mp="3cegf125qeusq">
                    This website may embed videos from YouTube. The provider is
                    Google Ireland Limited, Gordon House, Barrow Street, Dublin
                    4, Ireland. Further information is available in
                    Google&apos;s privacy policy:{' '}
                    <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline" data-id="element-682" data-mp="2ti6qtm148nv0">
                      https://policies.google.com/privacy?hl=en
                    </a>
                    .
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-683" data-mp="bk8lyimjn88i">
                    Google Web Fonts
                  </h3>
                  <p className="leading-relaxed" data-id="element-684" data-mp="gctbh7yf2z68">
                    This website uses web fonts provided by Google to display
                    fonts consistently. When you open a page, your browser loads
                    the required web fonts into its cache so that text and fonts
                    display correctly.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-[#0a0a0a] border-t border-white/5 py-8" data-id="element-685" data-mp="lablz2s31mgc">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-686" data-mp="lced5q4z1lmn">
            <p className="text-gray-600 text-sm" data-id="element-687" data-mp="pac1kqqx43oz">
              © {new Date().getFullYear()} Close CRM Agentur. All rights
              reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}